import {
  useState,
  MouseEvent,
  useEffect,
  ChangeEvent,
  useCallback
} from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  TableContainer,
  TablePagination,
  Card,
  Table,
  TableRow,
  TableBody,
  LinearProgress
} from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { styled } from '@mui/material/styles';
import { FaLongArrowAltUp, FaLongArrowAltDown } from 'react-icons/fa';
import { Search } from 'src/components/Search';
import { UserList } from 'src/api/user/data/interfaces/users-list';
import { getUsersList } from 'src/api/user/usecases/get-users-list';
import { getUsersLength } from 'src/api/user/usecases/get-users-list-length';
import { getBusinessesNames } from 'src/api/businesses/usecases/get-businesses-names.usecase';
import {
  searchUsersBy,
  searchUsersByReferral,
  searchUsersByPhone
} from 'src/api/user/usecases/search-users-by';
import CustomStyledTableRow from './CustomStyledTableRow';
import {
  StyledTableHead,
  HeaderCell,
  HeaderCellStatic
} from 'src/styles/components/Table.styled';

type FilterField =
  | 'email'
  | 'created'
  | 'lastVisit'
  | 'countOfPurchases'
  | 'amountOfPurchases';

interface SearchField {
  field: string;
  label: string;
}

const SEARCH_FIELDS: SearchField[] = [
  { field: 'email', label: 'User email' },
  { field: 'referral', label: 'Referral codes' },
  { field: 'phone', label: 'Phone number' }
];

const PLATFORM_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
  { value: 'web', label: 'WebFlow' }
];

const PROVIDER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'apple.com', label: 'Apple' },
  { value: 'google.com', label: 'Google' },
  { value: 'facebook.com', label: 'Facebook' },
  { value: 'password', label: 'Email & Password' }
];

const ROWS_PER_PAGE_OPTIONS = [
  { label: '7', value: 7 },
  { label: '20', value: 20 },
  { label: '100', value: 100 },
  { label: '500', value: 500 }
];

const PageWrapper = styled('div')({
  margin: '2vw'
});

const StyledSelect = styled(Select)({
  color: 'white',
  fontWeight: 600,
  fontSize: 12,
  textAlign: 'center'
});

const StyledInputLabel = styled(InputLabel)({
  color: 'white',
  fontWeight: 700,
  fontSize: 12,
  marginLeft: 6
});

const LoadingRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.background.default
  },
  '&:last-child td, &:last-child th': {
    border: 0
  }
}));

interface SortArrowProps {
  active: boolean;
  ascending: boolean;
}

const SortArrow = ({ active, ascending }: SortArrowProps) => {
  if (!active) return null;
  return ascending ? (
    <FaLongArrowAltUp style={{ marginBottom: -2 }} color="white" />
  ) : (
    <FaLongArrowAltDown style={{ marginBottom: -2 }} color="white" />
  );
};

interface FilterSelectProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (event: SelectChangeEvent) => void;
  disabled?: boolean;
}

const FilterSelect = ({
  label,
  value,
  options,
  onChange,
  disabled
}: FilterSelectProps) => {
  if (disabled) {
    return <Box>{label}</Box>;
  }

  return (
    <FormControl sx={{ m: 1, minWidth: 120, marginTop: 0.1 }}>
      <StyledInputLabel>{label}</StyledInputLabel>
      <StyledSelect variant="standard" value={value} onChange={onChange}>
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </StyledSelect>
    </FormControl>
  );
};

function Users() {
  const [users, setUsers] = useState<UserList[]>([]);
  const [searchBy, setSearchBy] = useState<string>(SEARCH_FIELDS[0].field);
  const [searchUsers, setSearchUsers] = useState<UserList[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [order, setOrder] = useState<boolean>(false);
  const [filteredField, setFilteredField] = useState<FilterField>('created');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [query, setQuery] = useState('');
  const [totalSize, setTotalSize] = useState(0);
  const [searchTotalSize, setSearchTotalSize] = useState(0);
  const [providerValue, setProviderValue] = useState<string>('');
  const [platformValue, setPlatformValue] = useState<string>('');
  const [businessNames, setBusinessNames] = useState<Record<string, string>>(
    {}
  );

  const isSearchMode = query.length > 0;
  const displayedUsers = isSearchMode ? searchUsers : users;

  const handleChangeProvider = useCallback(
    async (event: SelectChangeEvent) => {
      const value = event.target.value;
      setProviderValue(value);

      if (value === '') {
        setTimeout(() => (document.activeElement as HTMLElement).blur(), 0);
      }

      const [_users, size] = await Promise.all([
        getUsersList({
          provider: 'provider',
          value,
          filteredField,
          order,
          rowsPerPage
        }),
        getUsersLength({
          provider: 'provider',
          value,
          filteredField,
          order
        })
      ]);

      setTotalSize(size);
      setUsers(_users);
      setIsLoading(false);
      setPlatformValue('');
      setPage(0);
    },
    [filteredField, order, rowsPerPage]
  );

  const handleChangePlatform = useCallback(
    async (event: SelectChangeEvent) => {
      const value = event.target.value;
      setPlatformValue(value);

      if (value === '') {
        setTimeout(() => (document.activeElement as HTMLElement).blur(), 0);
      }

      const [_users, size] = await Promise.all([
        getUsersList({
          provider: 'platform',
          value,
          filteredField,
          order,
          rowsPerPage
        }),
        getUsersLength({
          provider: 'platform',
          value,
          filteredField,
          order
        })
      ]);

      setTotalSize(size);
      setUsers(_users);
      setIsLoading(false);
      setProviderValue('');
      setPage(0);
    },
    [filteredField, order, rowsPerPage]
  );

  const handleChangePage = useCallback(
    (_: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      setPage(newPage);
    },
    []
  );

  const handleChangeRowsPerPage = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    []
  );

  const handleColumnClick = useCallback(
    (field: FilterField) => {
      if (isSearchMode) return;
      setOrder(filteredField === field ? !order : false);
      setFilteredField(field);
      setPage(0);
    },
    [isSearchMode, filteredField, order]
  );

  const handleSearchUsers = (
    searchBy: string,
    searchQuery: string,
    rowsPerPage: number,
    page: number,
    cursor?: UserList['doc']
  ): Promise<{
    totalSize: number;
    users: UserList[];
  }> => {
    switch (searchBy) {
      case 'referral':
        return searchUsersByReferral(searchQuery, rowsPerPage, page);
      case 'phone':
        return searchUsersByPhone(searchQuery, rowsPerPage, page);
      default:
        return searchUsersBy(
          'email',
          searchQuery.toLowerCase(),
          rowsPerPage,
          cursor
        );
    }
  };

  const fetchNextPage = useCallback(async () => {
    const _users = isSearchMode ? searchUsers : users;
    if ((page + 1) * rowsPerPage <= _users.length) return;

    if (isSearchMode) {
      const result = await handleSearchUsers(
        searchBy,
        query,
        rowsPerPage,
        page,
        searchUsers[searchUsers.length - 1]?.doc
      );
      setSearchUsers([...searchUsers, ...result.users]);
    } else {
      const filterField = providerValue
        ? 'provider'
        : platformValue
        ? 'platform'
        : null;
      const filterValue = providerValue || platformValue || null;

      const _newUsers = await getUsersList({
        provider: filterField,
        value: filterValue,
        filteredField,
        order,
        rowsPerPage,
        doc: users[users.length - 1].doc
      });

      setUsers([...users, ..._newUsers]);
    }

    setIsLoading(false);
  }, [
    isSearchMode,
    searchUsers,
    users,
    page,
    rowsPerPage,
    searchBy,
    query,
    providerValue,
    platformValue,
    filteredField,
    order
  ]);

  const handleSearch = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.length) {
        setQuery('');
        return;
      }

      const result = await handleSearchUsers(
        searchBy,
        searchQuery,
        rowsPerPage,
        page
      );

      setQuery(searchQuery);
      setSearchTotalSize(result.totalSize);
      setSearchUsers(result.users);
      setPage(0);
      setRowsPerPage(20);
    },
    [searchBy, rowsPerPage, page]
  );

  useEffect(() => {
    getBusinessesNames().then((list) => {
      setBusinessNames(
        list.reduce<Record<string, string>>((acc, { id, name }) => {
          acc[id] = name;
          return acc;
        }, {})
      );
    });
  }, []);

  // Fetch initial data on filter/sort change
  useEffect(() => {
    const fetchData = async () => {
      if (!filteredField) return;

      const filterField = providerValue
        ? 'provider'
        : platformValue
        ? 'platform'
        : null;
      const filterValue = providerValue || platformValue || null;

      const [_users, size] = await Promise.all([
        getUsersList({
          provider: filterField,
          value: filterValue,
          filteredField,
          order,
          rowsPerPage
        }),
        getUsersLength({
          provider: filterField,
          value: filterValue,
          filteredField,
          order
        })
      ]);

      setTotalSize(size);
      setUsers(_users);
      setIsLoading(false);
    };
    fetchData();
  }, [rowsPerPage, order, filteredField]);

  // Fetch next page on page change
  useEffect(() => {
    if (page) fetchNextPage();
  }, [page]);

  const paginatedUsers = displayedUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <PageWrapper>
      <Search
        searchByFields={SEARCH_FIELDS}
        onSubmit={handleSearch}
        onSearchByChange={setSearchBy}
        styles={{ marginRight: 10 }}
      />

      <Card sx={{ flexGrow: 1, mt: '15px' }}>
        <TableContainer>
          <Table>
            <StyledTableHead>
              <TableRow>
                <HeaderCell onClick={() => handleColumnClick('email')}>
                  Email
                  <SortArrow
                    active={!isSearchMode && filteredField === 'email'}
                    ascending={order}
                  />
                </HeaderCell>

                <HeaderCellStatic sx={{ width: '18%', textAlign: 'center' }}>
                  <FilterSelect
                    label="PLATFORM"
                    value={platformValue}
                    options={PLATFORM_OPTIONS}
                    onChange={handleChangePlatform}
                    disabled={isSearchMode}
                  />
                </HeaderCellStatic>

                <HeaderCellStatic sx={{ width: '12%', textAlign: 'center' }}>
                  referral Code
                </HeaderCellStatic>

                <HeaderCell
                  sx={{ width: '12%', textAlign: 'center' }}
                  onClick={() => handleColumnClick('created')}
                >
                  Created
                  <SortArrow
                    active={!isSearchMode && filteredField === 'created'}
                    ascending={order}
                  />
                </HeaderCell>

                <HeaderCell
                  sx={{ width: '20%', textAlign: 'center' }}
                  onClick={() => handleColumnClick('lastVisit')}
                >
                  Last visit
                  <SortArrow
                    active={!isSearchMode && filteredField === 'lastVisit'}
                    ascending={order}
                  />
                </HeaderCell>

                <HeaderCellStatic sx={{ width: '10%', textAlign: 'center' }}>
                  <FilterSelect
                    label="Signup"
                    value={providerValue}
                    options={PROVIDER_OPTIONS}
                    onChange={handleChangeProvider}
                    disabled={isSearchMode}
                  />
                </HeaderCellStatic>

                <HeaderCell
                  sx={{ textAlign: 'center' }}
                  onClick={() => handleColumnClick('countOfPurchases')}
                >
                  Count of Purchases
                  <SortArrow
                    active={
                      !isSearchMode && filteredField === 'countOfPurchases'
                    }
                    ascending={order}
                  />
                </HeaderCell>

                <HeaderCell
                  sx={{ width: '12%', textAlign: 'center' }}
                  onClick={() => handleColumnClick('amountOfPurchases')}
                >
                  Total
                  <SortArrow
                    active={
                      !isSearchMode && filteredField === 'amountOfPurchases'
                    }
                    ascending={order}
                  />
                </HeaderCell>
              </TableRow>
            </StyledTableHead>

            <TableBody>
              {isLoading ? (
                <LoadingRow hover>
                  <Box sx={{ width: '100vw' }}>
                    <LinearProgress />
                  </Box>
                </LoadingRow>
              ) : (
                paginatedUsers.map((user, index) => (
                  <CustomStyledTableRow
                    key={index}
                    user={user}
                    businessNames={businessNames}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box p={2}>
          <TablePagination
            component="div"
            count={isSearchMode ? searchTotalSize : totalSize}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Card>
    </PageWrapper>
  );
}

export default Users;
